import { Box, CustomTheme, Stack, useTheme } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { matchPath, useLocation } from 'react-router-dom';
import NotesPopover from './NotesPopover';
import ChatPopover from 'op-pages/RO/AiSummary/components/ChatPopover';
import { useActionPopoverContext, UserContext } from 'op-contexts';
import { PermissionType } from 'op-enums';
import { getConversationSelectionStorageKey } from 'op-pages/RO/AiSummary/components/utils/chatHooks';
import { safeLocalStorageRemoveItem } from 'op-pages/RO/AiSummary/components/utils/safeLocalStorage';
import QuickNotesOnboarding from './QuickNotesOnboarding';

const PATIENT_PATHS: { path: string; paramName: 'id' | 'patientId' }[] = [
  { path: '/radiation/patient/:id', paramName: 'id' },
  { path: '/navigator/patient/:id', paramName: 'id' },
];
const CAREPLAN_PATH = '/radiation/patient/:id/careplan/:careplanId/';
const TREATMENT_REVIEW_PATH = '/radiation/patient/:id/treatment-review/:groupId';
const SCRIBE_PATH = '/radiation/patient/:id/scribe';
const DOCUMENT_UPLOAD_PATH = '/:oncologyType/patient/:id/documents/upload';
const SUMMARY_PATH = '/:oncologyType/patient/:id/summary';
const ASKPAGE_PATH = '/radiation/patient/:id/chat';

const matchPatientPath = (pathname: string): string | undefined => {
  for (const { path, paramName } of PATIENT_PATHS) {
    const match = matchPath<Record<string, string>>(pathname, { path, exact: false, strict: false });
    const patientId = match?.params?.[paramName];
    if (patientId) return patientId;
  }
  return undefined;
};

export const ASK_POPOVER_FEATURE = 'Enable Ask Popover';
export const NOTES_POPOVER_FEATURE = 'Enable Notes Popover';

const PatientFloatingActionsHost = (): JSX.Element | null => {
  const theme = useTheme<CustomTheme>();
  const userContext = useContext(UserContext);
  const { hasFeature, hasPermission } = userContext;
  const hasAskPopoverFeature =
    Boolean(hasFeature(ASK_POPOVER_FEATURE)) && hasPermission(PermissionType.RADIATION_ACCESS);
  const hasNotesPopoverFeature = Boolean(hasFeature(NOTES_POPOVER_FEATURE));
  const location = useLocation();
  const { closeFloatingPanel, popoverAnchorRef, isFocusedModalOpen, activePanel, isDocumentUploadOpen } =
    useActionPopoverContext();
  const previousPatientIdRef = useRef<string | undefined>(undefined);

  // Extract values based on the current URL path
  // patientId: The current Patient ID
  // bottom: A spacing offset based on whether there's a footer or other elements to avoid
  // showAskFab: whether to render Ask FAB (feature + patient + not full Ask page)
  // showNotesFab: whether to render the Quick Notes FAB (feature + not suppressed on the summary)
  const { patientId, bottom, showAskFab, showNotesFab } = useMemo(() => {
    const bottomNormal = theme.spacing(2);
    const bottomLarge = theme.spacing(10);
    // Document Upload has a taller fixed footer than the other footer pages, so the
    // standard lift (bottomLarge) still leaves the FAB sitting on the Submit button (RO-521).
    const bottomDocumentUpload = theme.spacing(12);

    const askPageMatch = matchPath<{ id: string }>(location.pathname, {
      path: ASKPAGE_PATH,
      exact: false,
      strict: false,
    });

    if (askPageMatch?.params?.id) {
      return {
        patientId: askPageMatch.params.id,
        bottom: bottomNormal,
        showAskFab: false,
        showNotesFab: hasNotesPopoverFeature,
      };
    }

    // Document Upload (RO and PSO) needs the FAB lifted clear of its taller footer.
    const documentUploadMatch = matchPath<{ id: string }>(location.pathname, {
      path: DOCUMENT_UPLOAD_PATH,
      exact: false,
      strict: false,
    });
    if (documentUploadMatch?.params?.id) {
      const patientId = documentUploadMatch.params.id;
      return {
        patientId,
        bottom: bottomDocumentUpload,
        showAskFab: hasAskPopoverFeature && Boolean(patientId),
        showNotesFab: hasNotesPopoverFeature,
      };
    }

    // Other pages with static footers need extra bottom spacing for the Ask FAB.
    const footerMatch = [CAREPLAN_PATH, TREATMENT_REVIEW_PATH, SCRIBE_PATH]
      .map((path) =>
        matchPath<{ id: string }>(location.pathname, {
          path,
          exact: false,
          strict: false,
        }),
      )
      .find((match) => match?.params?.id);
    if (footerMatch?.params?.id) {
      const patientId = footerMatch.params.id;
      return {
        patientId,
        bottom: bottomLarge,
        showAskFab: hasAskPopoverFeature && Boolean(patientId),
        showNotesFab: hasNotesPopoverFeature,
      };
    }

    const patientId = matchPatientPath(location.pathname);
    if (patientId) {
      // The Patient Summary keeps the page but suppresses the Quick Notes FAB (RO-521) — except
      // when the inline Document Upload view is open (PSO has no dedicated upload URL), where the
      // FAB must show and lift clear of the upload footer just like the standalone upload page.
      const isSummary = Boolean(
        matchPath<{ id: string }>(location.pathname, { path: SUMMARY_PATH, exact: false, strict: false })?.params?.id,
      );
      const isSummaryDocumentUpload = isSummary && isDocumentUploadOpen;
      return {
        patientId,
        bottom: isSummaryDocumentUpload ? bottomDocumentUpload : bottomNormal,
        showAskFab: hasAskPopoverFeature && Boolean(patientId),
        showNotesFab: hasNotesPopoverFeature && (!isSummary || isDocumentUploadOpen),
      };
    }

    return { patientId: undefined, bottom: bottomNormal, showAskFab: false, showNotesFab: false };
  }, [location.pathname, theme, hasAskPopoverFeature, hasNotesPopoverFeature, isDocumentUploadOpen]);

  useEffect(() => {
    const previousPatientId = previousPatientIdRef.current;

    // Close popover and clear localStorage when navigating away from a patient page
    if (!patientId) {
      if (previousPatientId) {
        safeLocalStorageRemoveItem(getConversationSelectionStorageKey(previousPatientId));
      }
      closeFloatingPanel();
    } else if (previousPatientId && previousPatientId !== patientId) {
      closeFloatingPanel();
    }

    previousPatientIdRef.current = patientId;
  }, [closeFloatingPanel, patientId]);

  const positionKey = `${String(bottom)}:${theme.spacing(2)}`;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const setAnchor = useCallback(
    (el: HTMLDivElement | null) => {
      (popoverAnchorRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      setAnchorEl(el);
    },
    [popoverAnchorRef],
  );

  // Only show the FABs if we are on a URL that contains a patientId
  if (!patientId) {
    return null;
  }

  // Nothing to render (e.g. Quick Notes suppressed on the summary and Ask not enabled).
  if (!showNotesFab && !showAskFab) {
    return null;
  }

  const floatingActions = (
    <Box
      data-testid="patient-floating-actions"
      sx={{
        position: 'fixed',
        bottom,
        right: theme.spacing(2),
        zIndex: isFocusedModalOpen ? theme.zIndex.modal - 2 : theme.zIndex.fab,
        transition: 'bottom 0.3s ease-in-out, right 0.3s ease-in-out',
      }}>
      <Stack ref={setAnchor} direction="column" alignItems="flex-end" spacing={1}>
        {showNotesFab && <NotesPopover patientId={patientId} positionKey={positionKey} soloFab={!showAskFab} />}
        {showAskFab && <ChatPopover patientId={patientId} positionKey={positionKey} />}
      </Stack>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: 56,
          height: 56,
          pointerEvents: 'none',
          opacity: 0,
          zIndex: theme.zIndex.fab - 2,
        }}
      />
      {showNotesFab && <QuickNotesOnboarding anchorEl={anchorEl} shouldDismiss={activePanel === 'notes'} />}
    </Box>
  );

  if (typeof document === 'undefined') {
    return floatingActions;
  }
  return createPortal(floatingActions, document.body);
};

export default PatientFloatingActionsHost;
